import React, { useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { defineMessages, FormattedMessage } from 'react-intl';

import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { addTaxIdentifier, setTaxIdentifiers } from '../store/tax-identifiers/index.actions';
import { LogService } from '../services/log-service';
import {
  getSellerTaxIdentifiers,
  deleteSellerTaxIdentifier as deleteSellerTaxIdentifierApi,
  addSellerTaxIdentifier as addSellerTaxIdentifierApi,
} from '../services/tax-identifiers/api';

const messages = defineMessages({
  errorTitle: {
    description: 'title for error notification',
    defaultMessage: 'Error',
    id: '/ztbDU',
  },
  deleteError: {
    description: 'message for error notification when deleting a tax id',
    defaultMessage: 'Failed deleting your tax identifier. Try again',
    id: '13JNm9',
  },
  addError: {
    description: 'message for error notification when adding a tax id',
    defaultMessage: 'Failed adding your tax identifier. Try again',
    id: 'yHuFWZ',
  },
});

export const useTaxIdentifiers = ({ includeInactive }) => {
  const taxIdentifiers = useSelector((state) => state?.taxIdentifiers);
  const dispatch = useDispatch();

  const fetchTaxIdentifiers = useCallback(() => {
    const fetch = async () => {
      try {
        const identifiers = await getSellerTaxIdentifiers({
          includeInactive: true,
        });
        dispatch(setTaxIdentifiers(identifiers));
      } catch (e) {
        LogService.logError(new Error('Error fetching seller tax identifiers', { cause: e }));
      }
    };
    return fetch();
  }, [dispatch]);

  const filteredTaxIdentifiers = useMemo(() => {
    const all = Object.values(taxIdentifiers ?? {});
    const onlyActive = all.filter((tId) => !!tId.active);
    return includeInactive ? all : onlyActive;
  }, [taxIdentifiers, includeInactive]);

  const deleteSellerTaxIdentifier = useCallback(
    async (arg) => {
      try {
        await deleteSellerTaxIdentifierApi(arg);
        fetchTaxIdentifiers();
      } catch (error) {
        LogService.logError(new Error('Error deleting seller tax identifier', { cause: error }));
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.errorTitle} />,
          content: <FormattedMessage {...messages.deleteError} />,
        });
      }
    },
    [fetchTaxIdentifiers],
  );

  const addSellerTaxIdentifier = useCallback(
    async (arg) => {
      try {
        const response = await addSellerTaxIdentifierApi(arg);
        dispatch(addTaxIdentifier(response));
      } catch (error) {
        LogService.logError(new Error('Error adding seller tax identifier', { cause: error }));
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.errorTitle} />,
          content: <FormattedMessage {...messages.addError} />,
        });
      }
    },
    [dispatch],
  );

  return useMemo(
    () => ({
      taxIdentifiers: filteredTaxIdentifiers,
      fetchTaxIdentifiers,
      addSellerTaxIdentifier,
      deleteSellerTaxIdentifier,
    }),
    [fetchTaxIdentifiers, filteredTaxIdentifiers, addSellerTaxIdentifier, deleteSellerTaxIdentifier],
  );
};
